import "./EmbededDocumentationPage.css";

import { Button, Icons, LoadingState, MessageState, Page } from "../../../../ds";
import { LoadError, LoadGate } from "../../../locks/components/LockStates";
import { lockErrorKind, lockService } from "../../../locks/services/LockService";
import { useCallback, useEffect, useState } from "react";

import { ExternalLinkButton } from "../../../locks/components/ExternalLinkButton";
import { LockMark } from "../../../locks/models/LockDTO";
import { PageContentRenderer } from "./PageContentRenderer";
import { PageHeader } from "./PageHeader";
import { PageService } from "../../services/PageService";
import axios, { CancelToken } from "axios";
import { resolvePageFormat } from "../../../../astromark";
import { spaceService } from "../../../spaces/services/SpaceService";
import { usePageMetadata } from "../../hooks/usePageMetadata";
import { useParams } from "react-router-dom";

const pageSvc = new PageService();

interface EmbeddedPage {
  title: string;
  pageContent?: string;
  dateModified: string;
  modifiedBy: string;
}

type PageState =
  | { status: "loading" }
  | { status: "ready"; page: EmbeddedPage }
  | { status: "empty" }
  | { status: "failed"; error: LoadError };

/**
 * The chrome-less page view for iframes in other apps (`/embeded/spaces/:spaceId/pages/:pageId`, board `Ark-Embedded`): no top bar and
 * no rails, the reader's header (space crumb, title with the lock glyph, meta, expedition) with "Open in Ark" and "Refresh", then the
 * body with its inline TOC. A hidden or missing page goes to Not found, as in the reader.
 */
export function EmbededDocumentationPage() {
  const { spaceId, pageId } = useParams();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [spaceName, setSpaceName] = useState<string>();
  const [lockMark, setLockMark] = useState<LockMark>();
  const page = state.status === "ready" ? state.page : undefined;
  const pageMetadata = usePageMetadata(pageId, page?.dateModified);
  const format = resolvePageFormat({ metadata: pageMetadata?.format });

  /****************************
   * DATA REQUESTS
   *****************************/

  /** The page as a `PageState`; `undefined` when the request was cancelled (the route moved on). */
  const fetchPage = useCallback((token?: CancelToken): Promise<PageState | undefined> => {
    if (!pageId || !spaceId) return Promise.resolve({ status: "empty" });
    return pageSvc.getPage(spaceId, pageId, token).then(
      (response): PageState => response
        ? { status: "ready", page: { title: response.title, pageContent: response.pageContent, dateModified: response.dateModified, modifiedBy: response.modifiedBy } }
        : { status: "empty" },
      (error): PageState | undefined => {
        if (axios.isCancel(error)) return undefined;
        // 404 = hidden (locked) or gone, the same answer for both; 503 = Identity unavailable
        const kind = lockErrorKind(error);
        return kind === "not-found" || kind === "identity-unavailable" ? { status: "failed", error: kind } : { status: "empty" };
      });
  }, [pageId, spaceId]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    fetchPage(source.token).then((next) => next && setState(next));
    return () => source.cancel();
  }, [fetchPage]);

  /** Try again after Identity was unavailable. */
  const retry = useCallback(() => {
    setState({ status: "loading" });
    fetchPage().then((next) => next && setState(next));
  }, [fetchPage]);

  // the space crumb and the lock glyph; failures leave them out
  useEffect(() => {
    if (!spaceId) return;
    let alive = true;
    spaceService().getById(spaceId).then((s) => alive && setSpaceName(s.title)).catch(() => undefined);
    if (pageId) {
      lockService()
        .getLock(spaceId, pageId)
        .then((s) => alive && setLockMark(s.locked ? { own: !s.inherited, lockPageId: s.pageId, lockTitle: s.title } : undefined))
        .catch(() => undefined);
    }
    return () => {
      alive = false;
    };
  }, [spaceId, pageId]);

  /****************************
   * USER ACTIONS
   *****************************/

  const handleRefreshClicked = useCallback(() => {
    window.location.reload();
  }, []);

  /****************************
   * CSS & HTML
   *****************************/

  // the page in the full app, on the host that serves this view (QA embeds open QA, prod embeds open prod)
  const arkUrl = `${window.location.origin}/spaces/${spaceId}/pages/${pageId}`;

  let content;
  if (state.status === "loading") {
    content = <LoadingState text="Loading the page…" />;
  } else if (state.status === "failed") {
    content = <LoadGate error={state.error} onRetry={retry}>{null}</LoadGate>;
  } else if (state.status === "empty" || !page) {
    content = <MessageState kind="add" title="Nothing here yet" text="Looks like this is empty! Try add a new page to this space!" />;
  } else {
    content = (
      <>
        <PageHeader
          title={page.title}
          expedition={pageMetadata?.expedition}
          tags={pageMetadata?.tags}
          author={page.modifiedBy}
          date={page.dateModified}
          lock={lockMark}
          trail={spaceName ? [{ text: spaceName }] : undefined}
        >
          <ExternalLinkButton href={arkUrl} text="Open in Ark" />
          <Button text="Refresh" type="secondary" size="sm" icon={<Icons.refresh />} onClick={handleRefreshClicked} />
        </PageHeader>
        <PageContentRenderer markdownContent={page.pageContent} format={format} hideToc={false} />
      </>
    );
  }

  return (
    <Page header={false} width="reading" className="ark-embedded-page" tabTitle={[page?.title, spaceName]}>
      {content}
    </Page>
  );
}
